/* eslint-disable */
// NOSONAR
import axios from 'axios';
import {
  ASSIGN_FORM_STATUS_URL,
  ASSIGN_SECTION_ACCESS_URL,
  ASSIGN_ENTITY_TYPE_WORKFLOW_STATUS_URL,
  ASSIGN_ENTITY_TYPE_WORKFLOW_FORM_URL,
  ASSIGN_STATUS_REVIEWERS_URL,
  CREATE_ACTION_URL,
  CREATE_APPLICATION_STATUS_APPROVAL_LOG_URL,
  CREATE_APPLICATION_TRANSITION_LOG_URL,
  CREATE_WORKFLOW_STATUS_TRANSITION_RULE_URL,
  CREATE_WORKFLOW_STATUS_APPROVAL_RULE_URL,
  DELETE_WORKFLOW_STATUS_APPROVAL_RULE_URL,
  CREATE_ENTITY_TYPE_WORKFLOW_URL,
  DELETE_TRANSITION_RULE_URL,
  DELETE_ACTION_URL,
  GET_ACTION_URL,
  GET_ALL_ACTIONS_URL,
  GET_ALL_TRANSITION_TYPES_URL,
  GET_ASSIGN_FORM_STATUS_DATA_URL,
  GET_FORM_WORKFLOW_TRANSITION_RULES_URL,
  GET_SECTION_ACCESS_URL,
  GET_STATUS_REVIEWERS_URL,
  GET_USER_ROLES_URL,
  GET_USERS_BY_ROLE_URL,
  MANAGE_FORM_WORKFLOW_URL,
  MANAGE_WORKFLOW_STATUS_TRANSITIONS_URL,
  UNASSIGN_ENTITY_TYPE_WORKFLOW_STATUS_URL,
  UNASSIGN_ENTITY_TYPE_WORKFLOW_FORM_URL,
  UNASSIGN_FORM_STATUS_URL,
  UNASSIGN_SECTION_ACCESS_URL,
  UNASSIGN_STATUS_REVIEWERS_URL,
  UPDATE_TRANSITION_RULE_URL,
  UPDATE_ACTION_URL,
  UPDATE_ENTITY_TYPE_WORKFLOW_URL,
  UPDATE_WORKFLOW_STATUS_APPROVAL_RULE_URL,
  VIEW_SELECTED_WORKFLOWS_URL,
  VIEW_ENTITY_TYPE_WORKFLOW_FORM_URL,
  VIEW_ENTITY_TYPE_WORKFLOWS_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import { onboarding_request_cache } from '@/services/onboarding_journey_service_helpers';

function parse_json_string(value, fallback = {}) {
  try { return JSON.parse(value); }
  catch { return fallback; }
}

function parse_response_data(response) {
  const raw_data = response?.data ?? response ?? {};

  if (typeof raw_data !== 'string') {
    return raw_data;
  }

  const parsed_data = parse_json_string(raw_data);

  return typeof parsed_data === 'string' ? parse_json_string(parsed_data) : parsed_data;
}

function clear_workflow_runtime_cache(payload = {}) {
  onboarding_request_cache.all_cases.clear();
  onboarding_request_cache.onboarding_status.clear();
  onboarding_request_cache.application_forms.clear();
  onboarding_request_cache.application_form.delete(String(payload?.application_form_id || ''));
  onboarding_request_cache.application_documents.clear();
  onboarding_request_cache.entity_profiles.clear();
}

function get_first_array(candidates) {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}

function get_first_non_empty_array(candidates) {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) {
      return candidate;
    }
  }

  return get_first_array(candidates);
}

function get_first_truthy_value(candidates) {
  for (const candidate of candidates) {
    if (candidate) {
      return candidate;
    }
  }

  return null;
}

function extract_items(response) {
  const parsed = parse_response_data(response);
  const items = get_first_array([
    parsed?.data?.list_entity_types,
    parsed?.data,
    parsed?.results,
    parsed,
  ]);

  return items;
}

function extract_workflow_statuses(response) {
  const parsed = parse_response_data(response);
  const items = get_first_non_empty_array([
    parsed?.data?.workflow_statuses,
    parsed?.data?.workflow_data,
    parsed?.data?.available_statuses,
    parsed?.data?.statuses,
    parsed?.results,
    parsed,
  ]);

  return (Array.isArray(items) ? items : []).map((status) => {
    const nested_status = status?.status && typeof status.status === 'object'
      ? status.status
      : null;

    return {
      ...status,
      status_id: status?.status_id ?? nested_status?.id ?? status?.id,
      status_name: status?.status_name ?? nested_status?.status ?? status?.name ?? status?.label ?? '',
      is_initial: typeof status?.is_initial === 'boolean' ? status.is_initial : Boolean(status?.initial),
      is_final: typeof status?.is_final === 'boolean' ? status.is_final : Boolean(status?.final)
    };
  }).filter((status) => {
    if (typeof status?.is_assigned === 'boolean') {
      return status.is_assigned;
    }

    return true;
  });
}

function extract_workflow_assignments(response) {
  const parsed = parse_response_data(response);
  const assignments = get_first_array([
    parsed?.data?.workflow_assigned_forms,
    parsed?.data?.assigned_forms,
    parsed?.workflow_assigned_forms,
    parsed?.assigned_forms,
  ]);

  return Array.isArray(assignments) ? assignments : [];
}

function extract_entity_workflow_forms(response) {
  const parsed = parse_response_data(response);
  const items = get_first_array([
    parsed?.data?.available_forms,
    parsed?.data?.data?.workflow_assigned_forms,
    parsed?.data?.data?.assigned_forms,
    parsed?.data?.data?.forms,
    parsed?.data?.workflow_assigned_forms,
    parsed?.data?.assigned_forms,
    parsed?.data?.forms,
    parsed?.workflow_assigned_forms,
    parsed?.assigned_forms,
    parsed?.forms,
    parsed?.data,
    parsed?.results,
  ]);

  return items;
}

function normalize_workflow_assignment(assignment) {
  const workflow_id = assignment?.workflow_id ?? assignment?.entity_type_workflow_id;

  if (!workflow_id) {
    return null;
  }

  const form_name = get_first_truthy_value([
    assignment?.form_name,
    assignment?.name,
    assignment?.title,
    assignment?.form_title,
    assignment?.form?.form_name,
    assignment?.form?.name,
    assignment?.form?.title,
  ]);

  if (!form_name) {
    return null;
  }

  const form_id = Number(get_first_truthy_value([
    assignment?.form_id,
    assignment?.form_company_id,
    assignment?.form?.id,
    assignment?.form?.form_company_id,
    0,
  ]));

  return {
    workflow_id,
    form: {
      id: form_id || null,
      name: form_name,
    },
  };
}

function get_workflow_identifier(workflow) {
  return Number(
    workflow?.id
    ?? workflow?.workflow_id
    ?? workflow?.entity_workflow_id
    ?? 0
  );
}

function get_assigned_form_names(assigned_forms, fallback_forms) {
  if (assigned_forms.length) {
    return assigned_forms.map((item) => item.name);
  }

  return Array.isArray(fallback_forms) ? fallback_forms : [];
}

function get_assigned_form_ids(assigned_forms) {
  return assigned_forms
    .map((item) => item.id)
    .filter(Boolean)
    .map(Number);
}

function map_workflow_with_assignments(workflow, assignments_by_workflow) {
  const workflow_id = get_workflow_identifier(workflow);
  const assigned_forms = assignments_by_workflow.get(workflow_id) || [];

  return {
    ...workflow,
    id: workflow_id || workflow?.id,
    workflow_id: workflow_id || workflow?.workflow_id,
    entity_workflow_id: workflow_id || workflow?.entity_workflow_id,
    assigned_forms: get_assigned_form_names(assigned_forms, workflow?.assigned_forms),
    assigned_form_ids: get_assigned_form_ids(assigned_forms),
  };
}

export const create_entity_type_workflow = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(CREATE_ENTITY_TYPE_WORKFLOW_URL, payload, request_config);

  return parse_response_data(response);
};

export const update_entity_type_workflow = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UPDATE_ENTITY_TYPE_WORKFLOW_URL, payload, request_config);

  return parse_response_data(response);
};

export const manage_form_workflow = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(MANAGE_FORM_WORKFLOW_URL, payload, request_config);

  return parse_response_data(response);
};

export const manage_workflow_status_transitions = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(MANAGE_WORKFLOW_STATUS_TRANSITIONS_URL, payload, request_config);

  return parse_response_data(response);
};

export const assign_entity_type_workflow_form = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(ASSIGN_ENTITY_TYPE_WORKFLOW_FORM_URL, payload, request_config);

  return parse_response_data(response);
};

export const assign_entity_type_workflow_status = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(ASSIGN_ENTITY_TYPE_WORKFLOW_STATUS_URL, payload, request_config);

  return parse_response_data(response);
};

export const unassign_entity_type_workflow_status = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UNASSIGN_ENTITY_TYPE_WORKFLOW_STATUS_URL, payload, request_config);

  return parse_response_data(response);
};

export const fetch_entity_type_workflow_forms = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(VIEW_ENTITY_TYPE_WORKFLOW_FORM_URL, payload, request_config);

  return extract_entity_workflow_forms(response);
};

export const unassign_entity_type_workflow_form = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UNASSIGN_ENTITY_TYPE_WORKFLOW_FORM_URL, payload, request_config);

  return parse_response_data(response);
};

export const fetch_selected_workflow = async (payload) => {
  const entity_workflow_id = String(payload?.entity_workflow_id ?? payload?.workflow_id ?? '');
  if (!entity_workflow_id) return null;
  if (onboarding_request_cache.workflow_details.has(entity_workflow_id)) {
    return onboarding_request_cache.workflow_details.get(entity_workflow_id);
  }

  const query = new URLSearchParams({ entity_workflow_id });
  const request_config = get_request_config();
  const request = axios.post(`${VIEW_SELECTED_WORKFLOWS_URL}?${query.toString()}`, {}, request_config)
    .then((response) => parse_response_data(response))
    .catch((error) => {
      onboarding_request_cache.workflow_details.delete(entity_workflow_id);
      throw error;
    });

  onboarding_request_cache.workflow_details.set(entity_workflow_id, request);
  return request;
};

export const fetch_workflow_statuses = async (payload) => {
  const entity_workflow_id = String(payload?.entity_workflow_id ?? payload?.workflow_id ?? '');
  if (!entity_workflow_id) return [];
  if (onboarding_request_cache.workflow_statuses.has(entity_workflow_id)) {
    return onboarding_request_cache.workflow_statuses.get(entity_workflow_id);
  }

  const query = new URLSearchParams({ entity_workflow_id });
  const request_config = get_request_config();
  const request = axios.post(`${VIEW_SELECTED_WORKFLOWS_URL}?${query.toString()}`, {}, request_config)
    .then((response) => extract_workflow_statuses(response))
    .catch((error) => {
      onboarding_request_cache.workflow_statuses.delete(entity_workflow_id);
      throw error;
    });

  onboarding_request_cache.workflow_statuses.set(entity_workflow_id, request);
  return request;
};

export const fetch_assign_form_status_data = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(GET_ASSIGN_FORM_STATUS_DATA_URL, payload, request_config);

  return parse_response_data(response);
};

export const assign_form_status = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(ASSIGN_FORM_STATUS_URL, payload, request_config);

  return parse_response_data(response);
};

export const unassign_form_status = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UNASSIGN_FORM_STATUS_URL, payload, request_config);

  return parse_response_data(response);
};

export const fetch_transition_types = async () => {
  const request_config = get_request_config();
  const response = await axios.post(GET_ALL_TRANSITION_TYPES_URL, {}, request_config);

  return parse_response_data(response);
};

export const fetch_form_workflow_transition_rules = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(GET_FORM_WORKFLOW_TRANSITION_RULES_URL, payload, request_config);

  return parse_response_data(response);
};

export const create_workflow_status_transition_rule = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(CREATE_WORKFLOW_STATUS_TRANSITION_RULE_URL, payload, request_config);

  return parse_response_data(response);
};

export const create_workflow_status_approval_rule = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(CREATE_WORKFLOW_STATUS_APPROVAL_RULE_URL, payload, request_config);

  return parse_response_data(response);
};

export const update_workflow_status_approval_rule = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UPDATE_WORKFLOW_STATUS_APPROVAL_RULE_URL, payload, request_config);

  return parse_response_data(response);
};

export const delete_workflow_status_approval_rule = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(DELETE_WORKFLOW_STATUS_APPROVAL_RULE_URL, payload, request_config);

  return parse_response_data(response);
};

export const update_transition_rule = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UPDATE_TRANSITION_RULE_URL, payload, request_config);

  return parse_response_data(response);
};

export const delete_transition_rule = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(DELETE_TRANSITION_RULE_URL, payload, request_config);

  return parse_response_data(response);
};

export const create_application_status_approval_log = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(CREATE_APPLICATION_STATUS_APPROVAL_LOG_URL, payload, request_config);

  return parse_response_data(response);
};

export const create_application_transition_log = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(CREATE_APPLICATION_TRANSITION_LOG_URL, payload, request_config);

  const parsed_response = parse_response_data(response);
  clear_workflow_runtime_cache(payload);

  return parsed_response;
};

export const fetch_users_by_role = async (params = {}) => {
  const request_config = get_request_config({ params });
  const response = await axios.post(GET_USERS_BY_ROLE_URL, {}, request_config);

  return parse_response_data(response);
};

export const fetch_user_roles = async (params = {}) => {
  const request_config = get_request_config({ params });
  const response = await axios.get(GET_USER_ROLES_URL, request_config);

  return parse_response_data(response);
};

export const assign_status_reviewers = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(ASSIGN_STATUS_REVIEWERS_URL, payload, request_config);

  return parse_response_data(response);
};

export const fetch_status_reviewers = async (params = {}) => {
  const request_config = get_request_config({ params });
  const response = await axios.get(GET_STATUS_REVIEWERS_URL, request_config);

  return parse_response_data(response);
};

export const unassign_status_reviewers = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UNASSIGN_STATUS_REVIEWERS_URL, payload, request_config);

  return parse_response_data(response);
};

export const assign_section_access = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(ASSIGN_SECTION_ACCESS_URL, payload, request_config);

  return parse_response_data(response);
};

export const fetch_section_access = async (params = {}) => {
  const request_config = get_request_config({ params });
  const response = await axios.get(GET_SECTION_ACCESS_URL, request_config);

  return parse_response_data(response);
};

export const unassign_section_access = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UNASSIGN_SECTION_ACCESS_URL, payload, request_config);

  return parse_response_data(response);
};

export const create_action = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(CREATE_ACTION_URL, payload, request_config);

  return parse_response_data(response);
};

export const fetch_all_actions = async () => {
  const request_config = get_request_config();
  const response = await axios.get(GET_ALL_ACTIONS_URL, request_config);

  return parse_response_data(response);
};

export const fetch_action = async (params = {}) => {
  const request_config = get_request_config({ params });
  const response = await axios.get(GET_ACTION_URL, request_config);

  return parse_response_data(response);
};

export const update_action = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(UPDATE_ACTION_URL, payload, request_config);

  return parse_response_data(response);
};

export const delete_action = async (payload) => {
  const request_config = get_request_config();
  const response = await axios.post(DELETE_ACTION_URL, payload, request_config);

  return parse_response_data(response);
};

export const fetch_entity_type_workflows = async (params = {}) => {
  const cache_key = JSON.stringify(params || {});
  if (onboarding_request_cache.entity_type_workflows.has(cache_key)) {
    return onboarding_request_cache.entity_type_workflows.get(cache_key);
  }

  const request_config = get_request_config({ params });
  const request = axios.get(VIEW_ENTITY_TYPE_WORKFLOWS_URL, request_config)
    .then((response) => {
      const workflows = extract_items(response);
      const assignments = extract_workflow_assignments(response);
      const assignments_by_workflow = assignments.reduce((accumulator, assignment) => {
        const normalized_assignment = normalize_workflow_assignment(assignment);

        if (!normalized_assignment) {
          return accumulator;
        }

        const current_assignments = accumulator.get(normalized_assignment.workflow_id) || [];
        current_assignments.push(normalized_assignment.form);
        accumulator.set(normalized_assignment.workflow_id, current_assignments);
        return accumulator;
      }, new Map());

      return workflows.map((workflow) => map_workflow_with_assignments(workflow, assignments_by_workflow));
    })
    .catch((error) => {
      onboarding_request_cache.entity_type_workflows.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.entity_type_workflows.set(cache_key, request);
  return request;
};
